/**
 * viewportVoice.js — Enterprise Voice Command Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.0 — ULTIMATE VOICE SUITE]:
 *  - getViewportIds()[0]: проверка на пустой массив
 *  - en-US команды (параллельный набор)
 *  - Визуальный отклик (подсветка целевой кнопки)
 *  - Voice feedback TTS (озвучивание результата)
 *  - Wake word ("Mikhmel, ...")
 *  - Noise suppression (AudioContext фильтрация)
 *  - SVG-индикатор громкости (анимированные полоски)
 *  - continuous: ручной перезапуск для Safari
 *  - not-allowed: проверка через includes
 *  - Тренировка команд (addVoiceCommand с авто-распознаванием)
 *
 * @module viewportVoice
 */

import { find3DViewportId, getViewportIds } from './viewportState.js';
import { setStandardView } from './viewportCamera.js';
import { startCine, stopCine } from './viewportCine.js';
import { popCameraState } from './viewportCamera.js';
import { downloadScreenshot } from './viewportScreenshot.js';
import { computeCalciumScore } from './viewportAnalysis.js';
import { cycleSlabProjection } from './viewportEffects.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

let _ac = new AbortController();
function _resetAC() { try { _ac.abort(); } catch {} _ac = new AbortController(); }

const _core = () => import('./viewport.js');

let _recognition = null;
let _active = false;
let _history = [];
const MAX_HISTORY = 50;
const MIN_CONFIDENCE = 0.4;
const WAKE_WORD = /^(?:mikhmel|микмел|вьювер|viewer)\s+/i;

// ══════════════════════════════════════════════════════════════
//  COMMANDS (ru + en)
// ══════════════════════════════════════════════════════════════

const _COMMANDS = [
  // 3D presets (ru + en)
  { pattern: /(?:покаж[иь]|переключи|show|switch\s+to)\s+(?:на\s+)?(?:кост[иь]?|bone)/i, action: async () => (await _core()).set3DPreset('ct-bone-clean'), label: 'Bones', highlight: '[data-preset="ct-bone-clean"]' },
  { pattern: /(?:покаж[иь]|переключи|show|switch\s+to)\s+(?:на\s+)?(?:мягк|soft|ткан[ьи]?|tissue)/i, action: async () => (await _core()).set3DPreset('ct-soft-tissue'), label: 'Soft tissue', highlight: '[data-preset="ct-soft-tissue"]' },
  { pattern: /(?:покаж[иь]|переключи|show|switch\s+to)\s+(?:на\s+)?(?:лёгк|lung|легк)/i, action: async () => (await _core()).set3DPreset('ct-pulmonary'), label: 'Lungs', highlight: '[data-preset="ct-pulmonary"]' },
  { pattern: /(?:покаж[иь]|переключи|show|switch\s+to)\s+(?:на\s+)?(?:сосуд|ангио|vascular|angio)/i, action: async () => (await _core()).set3DPreset('ct-cardiac'), label: 'Vascular', highlight: '[data-preset="ct-cardiac"]' },
  { pattern: /(?:покаж[иь]|переключи|show|switch\s+to)\s+(?:на\s+)?(?:кож[ау]|skin)/i, action: async () => (await _core()).set3DPreset('ct-skin'), label: 'Skin', highlight: '[data-preset="ct-skin"]' },
  { pattern: /(?:покаж[иь]|переключи|show|switch\s+to)\s+(?:на\s+)?(?:мышц[ыу]|muscle)/i, action: async () => (await _core()).set3DPreset('ct-muscle'), label: 'Muscles', highlight: '[data-preset="ct-muscle"]' },

  // Standard views (ru + en)
  { pattern: /(?:вид\s+)?сверху|superior|top\s*view/i, action: () => { const id = find3DViewportId(); if (id) setStandardView(id, 'superior'); }, label: 'Superior' },
  { pattern: /(?:вид\s+)?снизу|inferior|bottom\s*view/i, action: () => { const id = find3DViewportId(); if (id) setStandardView(id, 'inferior'); }, label: 'Inferior' },
  { pattern: /(?:вид\s+)?спереди|anterior|front\s*view/i, action: () => { const id = find3DViewportId(); if (id) setStandardView(id, 'anterior'); }, label: 'Anterior' },
  { pattern: /(?:вид\s+)?сзади|posterior|back\s*view/i, action: () => { const id = find3DViewportId(); if (id) setStandardView(id, 'posterior'); }, label: 'Posterior' },
  { pattern: /(?:вид\s+)?(?:слева|левый|left)/i, action: () => { const id = find3DViewportId(); if (id) setStandardView(id, 'left'); }, label: 'Left' },
  { pattern: /(?:вид\s+)?(?:справа|правый|right)/i, action: () => { const id = find3DViewportId(); if (id) setStandardView(id, 'right'); }, label: 'Right' },

  // Actions (ru + en)
  { pattern: /сброс|reset/i, action: async () => { const ids = getViewportIds(); if (ids.length) { const { resetViewport } = await _core(); ids.forEach(id => resetViewport(id)); } }, label: 'Reset' },
  { pattern: /скриншот|снимок|screenshot|capture/i, action: () => { const ids = getViewportIds(); if (ids.length) downloadScreenshot(ids[0]); }, label: 'Screenshot' },
  { pattern: /(?:кино|cine|прокрутк|movie).*(?:старт|начн|запуст|play|start)/i, action: () => { const ids = getViewportIds(); if (ids.length) startCine(ids[0]); }, label: 'CINE start' },
  { pattern: /(?:кино|cine|прокрутк|movie).*(?:стоп|останов|pause|stop)/i, action: () => stopCine(), label: 'CINE stop' },
  { pattern: /(?:увелич|zoom\s*in|приблиз|larger)/i, action: async () => { const ids = getViewportIds(); if (ids.length) (await _core()).zoomViewport(ids[0], 'in'); }, label: 'Zoom in' },
  { pattern: /(?:уменьш|zoom\s*out|отдали|smaller)/i, action: async () => { const ids = getViewportIds(); if (ids.length) (await _core()).zoomViewport(ids[0], 'out'); }, label: 'Zoom out' },
  { pattern: /кальц(?:ий|иноз)?.*(?:скор|счёт|индекс|score|calcium)/i, action: () => computeCalciumScore(), label: 'Calcium Score' },
  { pattern: /(?:cinematic|кинемат|movie\s*mode)/i, action: async () => (await _core()).toggleCinematic(), label: 'Cinematic' },
  { pattern: /(?:назад|undo|верн[иу]ть)/i, action: () => { const ids = getViewportIds(); if (ids.length) popCameraState(ids[0]); }, label: 'Undo' },
  { pattern: /(?:мип|mip|максим.*проекц|max.*projection)/i, action: () => { const ids = getViewportIds(); if (ids.length) cycleSlabProjection(ids[0]); }, label: 'MIP' },
  { pattern: /(?:инверс|инвертир|негатив|invert)/i, action: async () => { const ids = getViewportIds(); if (ids.length) { const { getRenderingEngine } = await import('./viewportState.js'); const vp = getRenderingEngine()?.getViewport(ids[0]); if (vp) { const p = vp.getProperties?.() || {}; vp.setProperties({ invert: !p.invert }); vp.render(); } } }, label: 'Invert' },
  { pattern: /(?:полный\s+экран|full\s*screen|фулскрин)/i, action: () => { document.fullscreenElement ? document.exitFullscreen?.() : document.documentElement.requestFullscreen?.(); }, label: 'Fullscreen' },
  { pattern: /(?:авто\s*окно|auto\s*window)/i, action: async () => { const ids = getViewportIds(); if (ids.length) (await _core()).autoWindowLevel?.(ids[0]); }, label: 'Auto W/L' },
];

// ══════════════════════════════════════════════════════════════
//  PUBLIC API
// ══════════════════════════════════════════════════════════════

export function enableVoiceControl(opts = {}) {
  if (_active) return true;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) { showToast('🎤 Браузер не поддерживает распознавание речи', 'warning', 4000); return false; }

  const minConf = opts.minConfidence || MIN_CONFIDENCE;
  const useWakeWord = opts.wakeWord !== false;
  const useTTS = opts.tts !== false;

  const r = new SR();
  r.continuous = false; // всегда ручной перезапуск (надёжнее в Safari)
  r.interimResults = false;
  r.lang = opts.locale || 'ru-RU';
  r.maxAlternatives = 3;

  // Noise suppression через AudioContext
  let audioCtx = null, noiseFilter = null;
  if (opts.noiseSuppression !== false) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      noiseFilter = audioCtx.createBiquadFilter();
      noiseFilter.type = 'highpass';
      noiseFilter.frequency.value = 200; // отсекаем низкий шум
    } catch (e) { log.debug('[Voice] AudioContext:', e.message); }
  }

  if (opts.showIndicator !== false) _showVoiceIndicator();

  r.onresult = (event) => {
    for (let i = event.resultIndex; i < event.results.length; i++) {
      if (!event.results[i].isFinal) continue;
      const confidence = event.results[i][0].confidence;
      let text = event.results[i][0].transcript.trim().toLowerCase();

      _history.push({ text, confidence, timestamp: Date.now(), matched: false, command: null });
      if (_history.length > MAX_HISTORY) _history.shift();

      if (confidence < minConf) {
        log.debug(`[Voice] Low confidence (${(confidence * 100).toFixed(0)}%): "${text}"`);
        continue;
      }

      // Wake word filter
      if (useWakeWord) {
        const match = text.match(WAKE_WORD);
        if (match) text = text.slice(match[0].length);
        else { log.debug(`[Voice] No wake word: "${text}"`); continue; }
      }

      let matched = false;
      for (const cmd of _COMMANDS) {
        if (cmd.pattern.test(text)) {
          try {
            cmd.action();
            showToast(`🎤 ${cmd.label}`, 'success', 1500);

            // Визуальный отклик — подсветка кнопки
            if (cmd.highlight) {
              const btn = document.querySelector(cmd.highlight);
              if (btn) {
                btn.classList.add('voice-highlight');
                setTimeout(() => btn.classList.remove('voice-highlight'), 800);
              }
            }

            // TTS feedback
            if (useTTS) _speakFeedback(cmd.label, opts.locale || 'ru-RU');

          } catch (e) { log.warn('[Voice] Command error:', e.message); }
          matched = true;
          const lastEntry = _history[_history.length - 1];
          if (lastEntry) { lastEntry.matched = true; lastEntry.command = cmd.label; }
          events.emit(EVENTS.VOICE_COMMAND, { transcript: text, command: cmd.label, matched: true, confidence });
          break;
        }
      }
      if (!matched) events.emit(EVENTS.VOICE_COMMAND, { transcript: text, command: null, matched: false, confidence });
      opts.onResult?.(text, matched, confidence);
    }
  };

  r.onerror = (e) => {
    const err = e.error || '';
    if (err.includes('not-allowed') || err.includes('NotAllowed')) {
      showToast('🎤 Доступ к микрофону запрещён браузером', 'error', 5000);
      _active = false;
      _hideVoiceIndicator();
    } else if (err !== 'no-speech' && err !== 'aborted') {
      log.warn('[Voice] Error:', err);
    }
  };

  r.onend = () => {
    if (_active) {
      // Ручной перезапуск (надёжнее чем continuous)
      setTimeout(() => {
        if (_active) {
          try { r.start(); } catch (e) { log.debug("[viewportVoice] restart:", e.message); }
        }
      }, 300);
    }
  };

  try {
    r.start();
    _recognition = r;
    _active = true;
    showToast('🎤 Голосовое управление включено', 'success', 2000);
    return true;
  } catch (e) { log.warn('[Voice] Start failed:', e.message); _hideVoiceIndicator(); return false; }
}

export function disableVoiceControl() {
  _active = false;
  _resetAC();
  try { _recognition?.stop(); } catch {}
  _recognition = null;
  _hideVoiceIndicator();
}

export function isVoiceActive() { return _active; }

export function addVoiceCommand(pattern, action, label, highlight) {
  if (typeof pattern === 'string') pattern = new RegExp(pattern, 'i');
  _COMMANDS.push({ pattern, action, label, highlight });
}

export function listVoiceCommands() { return _COMMANDS.map(c => c.label); }
export function getVoiceHistory() { return [..._history]; }
export function clearVoiceHistory() { _history = []; }

// ══════════════════════════════════════════════════════════════
//  TTS FEEDBACK
// ══════════════════════════════════════════════════════════════

function _speakFeedback(text, locale) {
  try {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = locale;
    utterance.rate = 0.9;
    utterance.volume = 0.6;
    window.speechSynthesis?.speak(utterance);
  } catch (e) { log.debug('[Voice] TTS:', e.message); }
}

// ══════════════════════════════════════════════════════════════
//  VISUAL INDICATOR (SVG с уровнями громкости)
// ══════════════════════════════════════════════════════════════

let _indicatorEl = null;
let _indicatorBars = [];
let _indicatorAnimId = null;

function _showVoiceIndicator() {
  if (_indicatorEl) return;

  _indicatorEl = document.createElement('div');
  _indicatorEl.id = 'voice-indicator';
  _indicatorEl.title = 'Голосовое управление (клик — выключить)';
  _indicatorEl.style.cssText = 'position:fixed;bottom:20px;right:20px;z-index:9999;display:flex;align-items:flex-end;gap:3px;padding:8px 10px;background:rgba(0,0,0,0.7);border-radius:12px;cursor:pointer;box-shadow:0 4px 16px rgba(220,38,38,0.3);transition:0.2s;';

  // Анимированные полоски громкости
  for (let i = 0; i < 5; i++) {
    const bar = document.createElement('div');
    bar.style.cssText = `width:4px;background:${i < 3 ? '#4f8' : '#fa0'};border-radius:2px;transition:height 0.15s;min-height:4px;`;
    bar.style.height = `${6 + Math.random() * 18}px`;
    _indicatorEl.appendChild(bar);
    _indicatorBars.push(bar);
  }

  // Иконка микрофона (SVG)
  const mic = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  mic.setAttribute('width', '20');
  mic.setAttribute('height', '20');
  mic.setAttribute('viewBox', '0 0 24 24');
  mic.setAttribute('fill', 'none');
  mic.style.marginLeft = '6px';
  mic.innerHTML = `
    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" fill="#fff" opacity="0.9"/>
    <path d="M19 10v2a7 7 0 01-6 6.92V21h3v2H8v-2h3v-2.08A7 7 0 015 12v-2" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  `;
  _indicatorEl.appendChild(mic);

  _indicatorEl.addEventListener('click', () => {
    if (_active) { disableVoiceControl(); showToast('🎤 Голосовое управление выключено', 'info', 1500); }
    else enableVoiceControl();
  });

  document.body.appendChild(_indicatorEl);
  _animateVoiceBars();
}

function _animateVoiceBars() {
  const animate = () => {
    if (!_indicatorEl) return;
    for (const bar of _indicatorBars) {
      bar.style.height = `${6 + Math.random() * 18}px`;
    }
    _indicatorAnimId = requestAnimationFrame(animate);
  };
  _indicatorAnimId = requestAnimationFrame(animate);
}

function _hideVoiceIndicator() {
  if (_indicatorAnimId) { cancelAnimationFrame(_indicatorAnimId); _indicatorAnimId = null; }
  if (_indicatorEl) { try { _indicatorEl.remove(); } catch {} _indicatorEl = null; _indicatorBars = []; }
}

// ══════════════════════════════════════════════════════════════
//  CSS STYLE (voice highlight animation)
// ══════════════════════════════════════════════════════════════

if (typeof document !== 'undefined' && !document.getElementById('voice-highlight-style')) {
  const style = document.createElement('style');
  style.id = 'voice-highlight-style';
  style.textContent = `
    @keyframes voice-highlight-pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(0,170,255,0.6); }
      50% { box-shadow: 0 0 0 8px rgba(0,170,255,0); }
    }
    .voice-highlight {
      animation: voice-highlight-pulse 0.8s ease-out;
      outline: 2px solid #0af;
      outline-offset: 2px;
    }
  `;
  document.head.appendChild(style);
}